// Port of ios/Sources/ViewModels/CreateChannelForm.swift.
//
// The pure half of creating a channel (shared/API.md §19.3): turning what a person typed into
// something the server will accept, and turning what the server said back into something a
// person can act on.
//
// WHY THE SLUG IS DERIVED AND NOT ASKED FOR. Mattermost stores two names: `display_name`, which
// is read, and `name`, the URL slug, which must be lowercase `a-z0-9-_`. On a phone, in a
// deployment that names its channels "Dirección General", a second field that silently rejects
// the accent the first one encouraged is a trap — so the slug is folded out of the display name
// and never shown.
import { characterCount, characters } from './swiftCharacters';

export const minDisplayName = 2;
export const maxDisplayName = 64;
export const minUrlLength = 2;
export const maxUrlLength = 64;

/** What the server was asked to create. 'public' is `"O"` on the wire, 'private' is `"P"`. */
export type Visibility = 'public' | 'private';

export function wireType(visibility: Visibility): 'O' | 'P' {
  return visibility === 'public' ? 'O' : 'P';
}

/**
 * Why a creation failed, in terms the sheet can put in a sentence. 'nameTaken' — 400: with a
 * slug this object generated, effectively always "that URL exists". 'notPermitted' — 403:
 * `create_public_channel` / `create_private_channel` not held.
 */
export type Failure = 'nameTaken' | 'notPermitted' | 'other';

/**
 * Foundation's `folding(options: [.diacriticInsensitive, .caseInsensitive])`: decompose, drop the
 * combining marks, lowercase.
 */
function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/**
 * The URL slug for a display name: accents folded (`Dirección` → `direccion`), lowercased,
 * anything else run together into single dashes, trimmed. Returns `""` when nothing survives — an
 * all-emoji name folds to nothing, and the caller must substitute `generatedName` rather than
 * send a slug the server will reject.
 */
export function urlName(displayName: string): string {
  let slug = '';
  let lastWasDash = false;
  for (const character of characters(fold(displayName))) {
    if (/^[A-Za-z0-9_]$/.test(character)) {
      slug += character.toLowerCase();
      lastWasDash = false;
    } else if (!lastWasDash) {
      // Runs of anything else — spaces, punctuation, literal dashes — become ONE dash.
      slug += '-';
      lastWasDash = true;
    }
  }
  slug = slug.replace(/^-+/, '').replace(/-+$/, '');
  slug = slug.slice(0, maxUrlLength);
  return slug.replace(/-+$/, '');
}

/**
 * A slug that is always valid: the derived one when it survives folding, otherwise one built
 * from `seed` (the clock, at the call site). The name is invisible to the user either way, so a
 * generated slug costs nothing and is strictly better than a 400 they cannot act on.
 */
export function nameForCreate(displayName: string, seed: number): string {
  const slug = urlName(displayName);
  return slug.length >= minUrlLength ? slug : generatedName(seed);
}

/** Base-36 rather than decimal so the tail stays short; ASCII by construction. */
export function generatedName(seed: number): string {
  return 'canal-' + Math.trunc(seed).toString(36);
}

/**
 * Whether the confirm action is available at all. Trimmed, because a name of spaces is not a
 * name and the server would reject it after a round trip.
 */
export function isSubmittable(displayName: string): boolean {
  const length = characterCount(displayName.trim());
  return length >= minDisplayName && length <= maxDisplayName;
}

export function failure(statusCode: number | null | undefined): Failure {
  switch (statusCode) {
    case 400:
      return 'nameTaken';
    case 403:
      return 'notPermitted';
    default:
      return 'other';
  }
}

export const CreateChannelForm = {
  minDisplayName,
  maxDisplayName,
  minUrlLength,
  maxUrlLength,
  wireType,
  urlName,
  nameForCreate,
  generatedName,
  isSubmittable,
  failure,
} as const;
